import React, {FC, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {useInterval} from '../../../../../srcCommon/helpers/hookHelpers';
import {Skeleton} from '../../../../components/common/skeleton';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';

/*
 * Constants.
 */

const autoSwitchIntervalTime = 5000; // 5 seconds.

/*
 * Props.
 */

interface SettingsAppsDetailsImageViewerProps {
  integrationType?: IntegrationTypeModel;
  isLoading: boolean;
}

/*
 * Style.
 */

const StyledImageViewerWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 16px;
`;

const StyledSelectedImageViewerDiv = styled.div`
  display: flex;
  align-items: center;
  height: 380px;
  border-radius: 8px;
`;

const StyledSelectedImg = styled.img`
  display: block;
  margin: auto;
  max-height: 380px;
  width: 100%;
  border-radius: 8px;
  background: ${(p) => p.theme.greys.shade20};
`;

const StyledImageSelectorWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

interface StyledSelectorItemDivProps {
  $isSelected: boolean;
}

const StyledSelectorItemDiv = styled.div<StyledSelectorItemDivProps>`
  width: 40px;
  height: 30px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 3px solid ${(p) => p.theme.greys.shade10};
  background: ${(p) => p.theme.greys.shade20};

  &:hover {
    border: 3px solid ${(p) => p.theme.alphas.black40};
  }
  ${(p) =>
    p.$isSelected &&
    css`
      border: 3px solid ${p.theme.palette.blue.shade40};

      &:hover {
        border: 3px solid ${p.theme.palette.blue.shade40};
      }
    `};
`;

const StyledSelectorImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsDetailsImageViewer: FC<SettingsAppsDetailsImageViewerProps> = (props) => {
  const {integrationType, isLoading} = props;

  const companyUrl = useAppSelector(getCompanyUrl);
  const [selectedImage, setSelectedImage] = useState(integrationType?.screenshots[0] || '');
  const [setSafeInterval, clearSafeInterval] = useInterval();

  const moveToNextSelectedImage = useCallback(() => {
    setSelectedImage((image) => {
      // If we do not have an integrationType or image, fall back to first or nothing.
      if (!integrationType || !image) {
        return integrationType?.screenshots[0] || '';
      }

      // Check the index of the current image and make sure the next index is valid.
      const indexOfImage = integrationType.screenshots.indexOf(image) || 0;
      if (indexOfImage + 1 < integrationType.screenshots.length) {
        return integrationType.screenshots[indexOfImage + 1];
      }
      return integrationType.screenshots[0];
    });
  }, [integrationType]);

  useEffect(() => {
    if (!selectedImage) {
      return;
    }

    // Each time the image is updated, we can clear the previous interval and set
    // up a new one. This allows us to manually set the image and reset the interval.
    clearSafeInterval();
    setSafeInterval(() => {
      moveToNextSelectedImage();
    }, autoSwitchIntervalTime);
  }, [moveToNextSelectedImage, selectedImage, setSafeInterval, clearSafeInterval]);

  if (isLoading || !integrationType) {
    return (
      <StyledImageViewerWrapperDiv>
        <Skeleton height="380px" borderRadius="8px" />
        <StyledImageSelectorWrapperDiv>
          <Skeleton width="40px" height="30px" borderRadius="8px" />
          <Skeleton width="40px" height="30px" borderRadius="8px" />
          <Skeleton width="40px" height="30px" borderRadius="8px" />
        </StyledImageSelectorWrapperDiv>
      </StyledImageViewerWrapperDiv>
    );
  }

  return (
    <StyledImageViewerWrapperDiv>
      <StyledSelectedImageViewerDiv>
        <StyledSelectedImg
          src={`${companyUrl}/integration_types/${integrationType.id}/public/screenshots/${selectedImage}`}
        />
      </StyledSelectedImageViewerDiv>
      {integrationType.screenshots.length > 1 && (
        <StyledImageSelectorWrapperDiv>
          {integrationType.screenshots.map((screenshot) => (
            <StyledSelectorItemDiv
              key={screenshot}
              $isSelected={screenshot === selectedImage}
              onClick={() => setSelectedImage(screenshot)}
            >
              <StyledSelectorImg
                src={`${companyUrl}/integration_types/${integrationType.id}/public/screenshots/${screenshot}`}
              />
            </StyledSelectorItemDiv>
          ))}
        </StyledImageSelectorWrapperDiv>
      )}
    </StyledImageViewerWrapperDiv>
  );
};
